/** "Team · Season" (#908 D1): a team is formed per season, so teams that
 * share a name differ by their season. A team from before seasons reads
 * `Team · {legacy}` where pickers mix it with season teams (admin surfaces
 * pass "Legacy"); leave `legacy` out where it should read as just its name. */
export function teamSeasonLabel(
  name: string,
  seasonName: string | null | undefined,
  legacy?: string,
): string {
  const season = seasonName || legacy;
  return season ? `${name} · ${season}` : name;
}

/** A seasoned team's season name from the club's season list (`/api/me`
 * `seasons`); "Season" while that list lacks it. `null` for a team from
 * before seasons. */
export function seasonNameOf(
  seasonId: string | null | undefined,
  seasons: ReadonlyArray<{ id: string; name: string }> | undefined,
): string | null {
  if (!seasonId) return null;
  return seasons?.find((season) => season.id === seasonId)?.name ?? "Season";
}

/** `teamSeasonLabel` for an admin team row, whose season is an id. */
export function adminTeamLabel(
  team: { name: string; season_id?: string | null },
  seasons: ReadonlyArray<{ id: string; name: string }> | undefined,
): string {
  return teamSeasonLabel(team.name, seasonNameOf(team.season_id, seasons), "Legacy");
}

/** Picker options whose labels still collide (two same-named teams in one
 * season, or in different clubs) get the team's slug, which is unique. */
export function withSlugWhenShared<T extends { label: string; slug: string }>(
  options: T[],
): T[] {
  const counts = new Map<string, number>();
  for (const option of options)
    counts.set(option.label, (counts.get(option.label) ?? 0) + 1);
  return options.map((option) =>
    (counts.get(option.label) ?? 0) > 1
      ? { ...option, label: `${option.label} · ${option.slug}` }
      : option,
  );
}

/** The teams a person can be added to, by name: an archived team is a past
 * season (#908) and takes no new members. */
export function openTeamsByName<T extends { name: string; status?: string }>(
  teams: ReadonlyArray<T>,
): T[] {
  return teams
    .filter((team) => team.status !== "archived")
    .sort((a, b) => a.name.localeCompare(b.name));
}
